import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  createView,
  updateView,
  deleteView,
  listViews,
  listDocumentVersionViews,
  getView,
  View,
  DocumentTypeChoices,
} from '@/lib/api/client'
import queryClient from '@/queryClient'
import { ViewInput } from '@/lib/api/client/types'
import MUTATION_KEYS from './mutationKeys'
import QUERY_KEYS from './queryKeys'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useToast } from '@/components/ui/use-toast'
import CustomError from '@/lib/api/CustomError'
import {
  listTemplateAttributesQuery,
  useUpdateTemplateAttribute,
} from '../template_attributes'
import { useListDocumentPagesQuery } from '../document_pages'

type mutationViewProps = {
  onSuccess?: (view?: View) => void
  onMutate?: () => void
}

/*
 ** Views in cache need to be updated after 1 minute
 ** of inactivity to update the download URL
 */
export const VIEWS_CACHE_TIME = 1 * 1000 * 60

export const useCreateView = (props?: mutationViewProps) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_VIEW],
    mutationFn: async ({
      cadVersionId,
      documentVersionId,
      values,
    }: {
      cadVersionId: string
      documentVersionId: string
      values: ViewInput
    }) => {
      const view = await createView(cadVersionId, values)
      return { view, documentVersionId }
    },
    onMutate: () => {
      if (props?.onMutate) props.onMutate()
    },
    onSuccess: ({ view }) => {
      const updatedView = {
        ...view,
        url: view.download_url,
      } as any
      const viewsUpdaterFn = (data: any) =>
        data ? [...data, updatedView] : [updatedView]
      queryClient.setQueriesData(
        {
          queryKey: [
            QUERY_KEYS.VIEWS,
            {
              cadVersionId: updatedView?.cad_version,
            },
          ],
        },
        viewsUpdaterFn,
      )

      if (props?.onSuccess) props.onSuccess(view)
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useUpdateView = (props?: mutationViewProps) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_VIEW],
    mutationFn: async ({
      viewId,
      values,
    }: {
      viewId: string
      values: ViewInput
    }) => {
      const view = await updateView(viewId, values)
      return { view }
    },
    onSuccess: async ({ view }: { view: View }) => {
      const updatedView: View & { url: string } = {
        ...view,
        url: view.download_url || '',
      }

      const viewsUpdaterFn = (data: any) =>
        data?.map((viewData) =>
          viewData.id === updatedView.id ? updatedView : viewData,
        )

      queryClient.setQueriesData(
        {
          queryKey: [
            QUERY_KEYS.VIEWS,
            {
              cadVersionId: updatedView?.cad_version,
            },
          ],
        },
        viewsUpdaterFn,
      )
      if (props?.onSuccess) props.onSuccess(view)
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useDeleteView = (props?: {
  onSuccess?: (viewId: string) => void
}) => {
  const { mutateAsync: updateTemplateAttribute } = useUpdateTemplateAttribute(
    {},
  )

  const queryClient = useQueryClient()
  const { data: docData } = useDocumentPageQuery()

  return useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_VIEW],
    mutationFn: async ({
      viewId,
      documentType,
    }: {
      viewId: string
      documentType: DocumentTypeChoices
    }) => {
      const isDocumentThemeEnabled = documentType === 'work_instructions'

      if (isDocumentThemeEnabled) {
        const view = await getViewQuery({ viewId })
        const documentPageId = view.document_page
        if (documentPageId) {
          const templateAttributes = await listTemplateAttributesQuery({
            documentPageId,
          })
          const viewTemplateAttributes = templateAttributes.filter(
            (attr) =>
              attr.data_type === 'view' &&
              attr.template_values.viewId === viewId,
          )

          for (const attr of viewTemplateAttributes) {
            await updateTemplateAttribute({
              templateAttributeId: attr.id as string,
              documentPageId,
              payload: {
                data_type: 'view',
                template_values: {
                  ...attr.template_values,
                  viewId: null,
                } as any,
              },
            })
          }
        }
      }

      await deleteView(viewId)

      return { viewId }
    },
    onSuccess: ({ viewId }) => {
      const queryKey = [QUERY_KEYS.VIEW, { viewId }]
      const viewsUpdaterFn = (data: any) =>
        data?.filter((view: any) => view.id !== viewId)

      queryClient.setQueriesData(
        {
          queryKey: [
            QUERY_KEYS.VIEWS,
            {
              cadVersionId: docData?.version?.id,
              documentVersionId: docData?.documentVersion?.id,
            },
          ],
        },
        viewsUpdaterFn,
      )

      queryClient.removeQueries({ queryKey })

      if (props?.onSuccess) props.onSuccess(viewId)
    },
  })
}

export const useListDocumentVersionViews = ({
  cadVersionId,
  documentVersionId,
}: {
  cadVersionId?: string | null
  documentVersionId?: string | null
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.VIEWS, { cadVersionId, documentVersionId }],
    queryFn: async () => {
      if (!cadVersionId || !documentVersionId) return []
      return listDocumentVersionViews(documentVersionId)
    },
    enabled: Boolean(cadVersionId && documentVersionId),
    staleTime: VIEWS_CACHE_TIME,
  })
}

export const useListViews = ({
  cadVersionId,
  enabled = true,
}: {
  cadVersionId?: string | null
  enabled?: boolean
}) => {
  const { toast } = useToast()

  return useQuery({
    queryKey: [QUERY_KEYS.VIEWS, { cadVersionId }],
    enabled: Boolean(cadVersionId) && enabled,
    queryFn: async () => {
      if (!cadVersionId) return []
      try {
        const views = await listViews(cadVersionId)
        return views
      } catch (e: any) {
        if (e instanceof CustomError) {
          toast({
            title: 'Error',
            description: e.message,
            variant: 'destructive',
          })
        }
        throw e
      }
    },
    staleTime: VIEWS_CACHE_TIME,
  })
}

export const useListPageViews = ({
  cadVersionId,
  documentVersionId,
  enabled = true,
}: {
  cadVersionId?: string | null
  documentVersionId?: string | null
  enabled?: boolean
}) => {
  const { data: documentPages, isLoading: isLoadingDocumentPages } =
    useListDocumentPagesQuery({
      documentVersionId,
    })
  const { data: views, isLoading: isLoadingViews } = useListViews({
    cadVersionId,
    enabled,
  })
  const documentPageIds = useMemo(
    () => new Set((documentPages || []).map((page) => page.id)),
    [documentPages],
  )
  const filteredViews = useMemo(
    () =>
      (views || []).filter(
        (view) => view.document_page && documentPageIds.has(view.document_page),
      ),
    [views, documentPageIds],
  )

  return {
    data: filteredViews,
    isLoading: isLoadingDocumentPages || isLoadingViews,
  }
}

export const listViewsQuery = (
  cadVersionId: string,
  documentVersionId: string,
) => {
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.VIEWS, { cadVersionId, documentVersionId }],
    queryFn: () => {
      if (!cadVersionId) return []
      return listViews(cadVersionId)
    },
    staleTime: VIEWS_CACHE_TIME,
  })
}

export const listPageViewsQuery = ({
  cadVersionId,
  documentVersionId,
}: {
  cadVersionId: string
  documentVersionId: string
}) => {
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.VIEWS, { cadVersionId, documentVersionId }],
    queryFn: () => {
      return listViews(cadVersionId)
    },
    staleTime: VIEWS_CACHE_TIME,
  })
}

export const getViewQuery = ({ viewId }: { viewId: string }) => {
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.VIEW, { viewId }],
    queryFn: () => getView(viewId),
  })
}

export const useGetView = ({ viewId }: { viewId?: string | null }) => {
  return useQuery({
    queryKey: [QUERY_KEYS.VIEW, { viewId }],
    queryFn: () => getView(viewId as string),
    enabled: Boolean(viewId),
  })
}

export { QUERY_KEYS, MUTATION_KEYS }
